import './CouponDetailsModal.scss';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  isCouponApplied,
  getOfferCategory,
  isExclusiveCoupon,
  hasExclusiveApplied,
  cartHasExclusiveItemOffer,
  bogoEqualOrLesserSatisfied,
  computeRawCartSubtotal,
} from '../../common/utils/offer-utils';
import PercentageImage from '../../common/assets/svg/percentageimg.svg';
import Dollarimg from '../../common/assets/svg/dollarimg.svg';
import freeItemimg from '../../common/assets/svg/freeitemimg.svg';
import freeDeliveryimg from '../../common/assets/svg/freedeliveryimg.svg';

const CouponDetailsModal = ({ couponDetails, open, onClose, onApply, hideActions }: any) => {
  const appliedCoupons = useSelector((state: any) => state.restaurant.appliedCoupons || []);
  const cart = useSelector((state: any) => state.cart.cart.items);
  const cartItemsFromApi = useSelector((state: any) => state.cart.cartItems);
  const restaurantMenuDetails = useSelector((state: any) => state.restaurant.restaurantMenuDetails);

  const disabledItemIds = useMemo(() => {
    if (!Array.isArray(cartItemsFromApi) || !Array.isArray(cart)) return [] as string[];
    const ids: string[] = [];
    cart.forEach((item: any) => {
      const apiItem = cartItemsFromApi.find((i: any) => i?.itemId === item?.itemId);
      if (apiItem && apiItem.enabled === false) ids.push(item.itemId);
    });
    return ids;
  }, [cartItemsFromApi, cart]);

  const availableSubTotal = useMemo(() => {
    const availableItems = (cart || []).filter(
      (item: any) => !disabledItemIds.includes(item?.itemId)
    );
    return computeRawCartSubtotal(availableItems);
  }, [cart, disabledItemIds]);

  // ── Same checks as CouponCard ──
  const disabledtheApplyBtn = availableSubTotal >= Number(couponDetails?.min_subtotal);
  const isThisCouponApplied =
    isCouponApplied(appliedCoupons, couponDetails?.id) ||
    isCouponApplied(appliedCoupons, couponDetails?.offerId);

  const itemLevelExclusiveActive = cartHasExclusiveItemOffer(cart, restaurantMenuDetails);
  const exclusiveActive = hasExclusiveApplied(appliedCoupons);
  const thisIsExclusive = isExclusiveCoupon(couponDetails);
  const couponCategory = getOfferCategory(couponDetails);
  // FREE_ITEM never occupies the exclusivity slot — it coexists with
  // everything, so it shouldn't count as "something else is applied".
  const hasAppliedOrderLevelCoupon = appliedCoupons.some(
    (c: any) => c?.id && getOfferCategory(c) !== 'FREE_ITEM'
  );
  // FREE_ITEM offers are exempt from every exclusivity check so they can
  // always be added once min_subtotal is met.
  const blockedByExclusivity = couponCategory !== 'FREE_ITEM' && !isThisCouponApplied && (
    exclusiveActive ||
    (thisIsExclusive && hasAppliedOrderLevelCoupon) ||
    itemLevelExclusiveActive
  );

  const isFreeItemOffer = couponCategory === 'FREE_ITEM';
  const freeListEmpty =
    isFreeItemOffer &&
    !(Array.isArray(couponDetails?.freeList) &&
      couponDetails.freeList.some((itemId: any) => {
        const m = Array.isArray(restaurantMenuDetails)
          ? restaurantMenuDetails.find((menu: any) => menu?.itemId === itemId)
          : null;
        return m && m.enable !== false;
      }));

  const isBogoOffer =
    String(couponDetails?.offer_type ?? couponDetails?.offerType ?? '').toUpperCase() === 'BOGO';
  const bogoNotEligible = (() => {
    if (!isBogoOffer) return false;
    const bogo = couponDetails?.bogo;
    if (!bogo) return true;
    const buyItemIds: string[] = (Array.isArray(bogo.buyItems) ? bogo.buyItems : [])
      .map((b: any) => b?.itemId).filter(Boolean);
    const getItemIds: string[] = (Array.isArray(bogo.getItems) ? bogo.getItems : [])
      .map((g: any) => g?.itemId).filter(Boolean);
    const buyQty = Number(bogo.buyQty || 1);
    if (buyItemIds.length === 0 || getItemIds.length === 0) return true;
    const cartArr = Array.isArray(cart) ? cart : [];
    if (!bogoEqualOrLesserSatisfied(bogo, cartArr, buyItemIds, getItemIds)) return true;
    const isSameItem = buyItemIds.some((id) => getItemIds.includes(id));
    if (isSameItem) {
      const totalSame = cartArr.reduce(
        (sum: number, item: any) =>
          buyItemIds.includes(item?.itemId) ? sum + Number(item?.quantity || 0) : sum,
        0
      );
      return totalSame < buyQty + 1;
    }
    let buyInCart = 0;
    let getInCart = 0;
    cartArr.forEach((item: any) => {
      const qty = Number(item?.quantity || 0);
      if (qty <= 0) return;
      if (buyItemIds.includes(item?.itemId)) {
        const isThisOffersGet =
          item?.offer_data?.offerId === couponDetails?.id && item?.is_discount_applied === true;
        buyInCart += isThisOffersGet ? 0 : qty;
      }
      if (getItemIds.includes(item?.itemId)) getInCart += qty;
    });
    return !(buyInCart >= buyQty && getInCart >= 1);
  })();

  const applyDisabled =
    !disabledtheApplyBtn || isThisCouponApplied || blockedByExclusivity || freeListEmpty || bogoNotEligible;

  // ── Icon (same logic as CouponCard) ──
  const isFlatFee = couponDetails?.offer_type === 'FLAT';
  let iconSrc: string;
  if (couponCategory === 'FREE_DELIVERY') {
    iconSrc = freeDeliveryimg;
  } else if (couponCategory === 'FREE_ITEM') {
    const firstFreeItemId = Array.isArray(couponDetails?.freeList) ? couponDetails.freeList[0] : null;
    const firstFreeMenuItem =
      firstFreeItemId && Array.isArray(restaurantMenuDetails)
        ? restaurantMenuDetails.find((m: any) => m?.itemId === firstFreeItemId)
        : null;
    const media = Array.isArray(firstFreeMenuItem?.itemMedia) ? firstFreeMenuItem.itemMedia[0] : null;
    const imageId = media?.itemImage ?? firstFreeMenuItem?.itemImage ?? null;
    const imageType = media?.itemType ?? firstFreeMenuItem?.itemType ?? null;
    iconSrc =
      imageId && imageType
        ? `${process.env.REACT_APP_IMAGE_URL}img/${imageId}.${String(imageType).split('/')[1] || imageType}`
        : freeItemimg;
  } else {
    iconSrc = isFlatFee ? Dollarimg : PercentageImage;
  }

  const restaurantDetails = useSelector((state: any) => state.restaurant.currentRestaurantDetail);
  const currencySymbol = restaurantDetails?.country === 'IN' ? '₹' : '$';
  const minSubtotalVal = Number(couponDetails?.min_subtotal);
  const freeItemFallbackDesc = (() => {
    if (couponCategory !== 'FREE_ITEM') return null;
    const firstId = Array.isArray(couponDetails?.freeList) ? couponDetails.freeList[0] : null;
    const menuItem = firstId && Array.isArray(restaurantMenuDetails)
      ? restaurantMenuDetails.find((m: any) => m?.itemId === firstId)
      : null;
    const itemName = menuItem?.itemName || null;
    return itemName
      ? (minSubtotalVal > 0
          ? `Get a free item on orders above ${currencySymbol}${minSubtotalVal.toFixed(2)}`
          : `Get a free item with your order`)
      : (minSubtotalVal > 0
          ? `Get a Free Item on orders above ${currencySymbol}${minSubtotalVal.toFixed(2)}`
          : `Get a Free Item with your order`);
  })();
  const [copied, setCopied] = useState(false);
  const isMobile = useSelector((state: any) => state?.navigation?.isMobile);

  const couponApply = () => {
    if (applyDisabled) return;
    onApply();
  };

  const handleCopy = () => {
    const code = couponDetails?.promoCodes?.length ?Array.isArray(couponDetails.promoCodes)
                  ? couponDetails.promoCodes[0]
                  : couponDetails.promoCodes:"";
    if (!code) return;
    const doFeedback = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(doFeedback).catch(() => {
        fallbackCopy(code);
        doFeedback();
      });
    } else {
      fallbackCopy(code);
      doFeedback();
    }
  };

  const fallbackCopy = (text: string) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    try { document.execCommand('copy'); } catch {}
    document.body.removeChild(el);
  };

  if (!open) return null;


  return (
    <div className="coupon-details-overlay" onClick={onClose}>
      <div className="coupon-details-modal" onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button type="button" className={`cdm-close ${isMobile ? 'cdm-close-M' : 'cdm-close-D'} `} aria-label="Close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header: icon + name */}
        <div className="coupon-details-header">
          <div className="cdm-icon-wrap">
            <img src={iconSrc} alt="" className="coupon-details-icon"
              onError={(e) => {
                if (couponCategory === 'FREE_ITEM') (e.currentTarget as HTMLImageElement).src = freeItemimg;
              }}
            />
          </div>
          <h3 className="coupon-code">{couponDetails?.name}</h3>
        </div>

        {/* Promo code chip */}
        {couponDetails?.promoCodes?.length>0 && (
          <div className="cdm-promo-row">
            <span className="cdm-promo-chip">{couponDetails.promoCodes[0]}</span>
            <span
              className={`cdm-copy-btn${copied ? ' cdm-copy-btn--copied' : ''}`}
              onClick={handleCopy}
            >
              {copied ? '✓ Copied!' : 'Copy Code'}
            </span>
          </div>
        )}

        <p className="coupon-desc">
          {couponDetails?.description || freeItemFallbackDesc || (() => {
            const offerType = String(couponDetails?.offer_type ?? couponDetails?.offerType ?? '').toUpperCase();
            const discountVal = parseFloat(couponDetails?.discount_value || couponDetails?.offerRate || '0');
            const minSub = Number(couponDetails?.min_subtotal || 0);
            const minText = minSub > 0 ? ` for order value above ${currencySymbol}${minSub.toFixed(2)}` : '';
            if (offerType === 'FLAT' || offerType === 'FLATFEE') return `Flat ${currencySymbol}${discountVal.toFixed(2)} Off${minText}`;
            if (offerType === 'BOGO') return couponDetails?.name || 'Buy One Get One';
            if (discountVal > 0) return `${Number.isInteger(discountVal) ? discountVal : discountVal}% Off${minText}`;
            return `Use ${couponDetails?.name} & save instantly on your order.`;
          })()}
        </p>
{
  !hideActions &&   <hr className="coupon-details-divider" />
}
      

        {!hideActions && (
          <div className="coupon-details-footer">
            <button
              type="button"
              className="coupon-details-btn coupon-details-btn--back"
              onClick={onClose}
            >
              Back
            </button>
            <button
              type="button"
              className={`coupon-details-btn coupon-details-btn--apply ${applyDisabled ? 'disabled-applybtn' : ''}`}
              onClick={couponApply}
              disabled={applyDisabled}
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponDetailsModal;
